/**
 * Deterministic Pseudonymous Name Generator
 * Generates unique three-word names for wallet addresses
 * Theme: Psychedelic/spiritual/cosmic aligned with Psanctuary Church
 */

/**
 * Simple deterministic hash function for wallet addresses
 * @param {string} str - The wallet address to hash
 * @returns {number} - A hash value
 */
const simpleHash = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
};

/**
 * Get word lists from config or use defaults
 * @param {object} config - Configuration object with nameGenerator settings
 * @returns {object} - Object containing word arrays
 */
const getWordLists = (config) => {
  const defaults = {
    adjectives1: [
      'Cosmic', 'Sacred', 'Mystic', 'Ethereal', 'Divine',
      'Celestial', 'Astral', 'Luminous', 'Radiant', 'Transcendent'
    ],
    adjectives2: [
      'Purple', 'Golden', 'Silver', 'Violet', 'Crimson',
      'Azure', 'Emerald', 'Amber', 'Indigo', 'Sapphire'
    ],
    nouns: [
      'Wanderer', 'Phoenix', 'Dreamer', 'Sage', 'Oracle',
      'Seeker', 'Voyager', 'Guardian', 'Shaman', 'Mystic'
    ]
  };

  if (config && config.nameGenerator) {
    return {
      adjectives1: config.nameGenerator.adjectives1 || defaults.adjectives1,
      adjectives2: config.nameGenerator.adjectives2 || defaults.adjectives2,
      nouns: config.nameGenerator.nouns || defaults.nouns
    };
  }

  return defaults;
};

/**
 * Generate a deterministic three-word pseudonymous name from a wallet address
 * @param {string} address - The wallet address (e.g., "0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb")
 * @param {object} config - Optional configuration object
 * @returns {string} - Three-word name (e.g., "Cosmic Purple Wanderer")
 */
export const generatePseudonym = (address, config = null) => {
  if (!address) return '';

  // Check localStorage first
  const storageKey = `pseudonym_${address.toLowerCase()}`;
  const stored = localStorage.getItem(storageKey);
  if (stored) return stored;

  // Get word lists
  const { adjectives1, adjectives2, nouns } = getWordLists(config);

  // Generate hash from address
  const hash = simpleHash(address.toLowerCase());

  // Use different parts of the hash for each word selection
  const adj1Index = hash % adjectives1.length;
  const adj2Index = Math.floor(hash / adjectives1.length) % adjectives2.length;
  const nounIndex = Math.floor(hash / (adjectives1.length * adjectives2.length)) % nouns.length;

  // Construct the name
  const name = `${adjectives1[adj1Index]} ${adjectives2[adj2Index]} ${nouns[nounIndex]}`;

  // Store in localStorage for persistence
  localStorage.setItem(storageKey, name);

  return name;
};

/**
 * Splice a wallet address for display
 * @param {string} address - The wallet address (e.g., "0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb")
 * @returns {string} - Spliced address (e.g., "0x742d...0bEb")
 */
export const spliceAddress = (address) => {
  if (!address) return '';
  if (address.length < 10) return address;
  
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

/**
 * Get the stored pseudonym for an address
 * @param {string} address - The wallet address
 * @returns {string|null} - The stored pseudonym or null if not found
 */
export const getStoredPseudonym = (address) => {
  if (!address) return null;
  const storageKey = `pseudonym_${address.toLowerCase()}`;
  return localStorage.getItem(storageKey);
};

/**
 * Clear the stored pseudonym for an address
 * @param {string} address - The wallet address
 */
export const clearPseudonym = (address) => {
  if (!address) return;
  const storageKey = `pseudonym_${address.toLowerCase()}`;
  localStorage.removeItem(storageKey);
};

/**
 * Get all stored pseudonyms
 * @returns {object} - Object mapping addresses to pseudonyms
 */
export const getAllPseudonyms = () => {
  const pseudonyms = {};
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('pseudonym_')) {
      const address = key.replace('pseudonym_', '');
      pseudonyms[address] = localStorage.getItem(key);
    }
  }
  return pseudonyms;
};

